<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Pending Applications', path: {name: 'admin-applications'} }]" :name="companyName" no-title/>
    <b-row>
      <b-col lg="6" md="8">
        <admin-panel with-header with-footer>
          <template v-slot:header>
            <h2 class="text-center mb-0">{{ companyName }}</h2>
          </template>
          <template v-slot:footer>
            <div class="text-center">
              <b-btn variant="success" @click.prevent="approveApplication">Approve Application</b-btn>
              <b-btn variant="danger" @click.prevent="rejectApplication">Reject Application</b-btn>
            </div>
          </template>
          <div v-if="!loading">
            <div class="mb-2">
              <h4 class="mb-0">Owner</h4>
              <span>{{ application.user.name }}</span>
            </div>
            <div class="mb-2">
              <h4 class="mb-0">Email</h4>
              <span>{{ application.user.email }}</span>
            </div>
            <div class="mb-2" v-if="application.user.phone">
              <h4 class="mb-0">Phone</h4>
              <span>{{ application.user.phone }}</span>
            </div>
            <div class="mb-2">
              <h4 class="mb-0">Date Applied</h4>
              <span>{{ dateApplied }}</span>
            </div>
            <div class="mb-2">
              <h4 class="mb-0">Facility License</h4>
              <span>{{ application.license.type.name + ' #' + application.license.number }}</span>
            </div>
            <div class="mb-2">
              <h4 class="mb-0">Facility Address</h4>
              <span>{{ addressStreetPart }}</span><br>
              <span>{{ addressCityPart }}</span>
            </div>
          </div>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-application-approve-modal ref="approveModal" @confirm="confirmApproveApplication"/>
    <admin-application-reject-modal ref="rejectModal" @confirm="confirmRejectApplication"/>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {convertFromUtc} from "../helpers";
import AdminApplicationRejectModal from "../components/AdminApplicationRejectModal";
import AdminApplicationApproveModal from "../components/AdminApplicationApproveModal";

export default {
  name: "AdminApplication",
  components: {AdminApplicationApproveModal, AdminApplicationRejectModal, AdminPanel, AdminBreadcrumb, AdminPreloader},
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      application: {},
      loading: true
    }
  },
  methods: {
    approveApplication() {
      this.$refs.approveModal.open();
    },
    confirmApproveApplication(approveFormData) {
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_APPLICATIONS}/${this.id}/approve`, approveFormData).then(({data}) => {
        this.$bvToast.toast('Company has been approved', {
          title: 'Success!',
          variant: 'success'
        });
        setTimeout(() => {
          this.$router.push({name: "admin-company", params: {id: data.company.id.toString()}});
        }, 1000);
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    rejectApplication() {
      this.$refs.rejectModal.open();
    },
    confirmRejectApplication(rejectFormData) {
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_APPLICATIONS}/${this.id}/reject`, rejectFormData).then(({data}) => {
        this.$bvToast.toast('Application has been rejected', {
          title: 'Success!',
          variant: 'success'
        });
        setTimeout(() => {
          this.$router.push({name: "admin-applications"});
        }, 1000);
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
        this.$refs.rejectModal.open();
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  computed: {
    companyName() {
      return this.application?.name ?? '';
    },
    dateApplied() {
      if (!this.application.created_at) {
        return '';
      }
      return convertFromUtc(this.application.created_at)
    },
    addressStreetPart() {
      if (!this.application.id) {
        return '';
      }
      let addressStreetParts = [];
      if (this.application.license.location.address_line_1) {
        addressStreetParts.push(this.application.license.location.address_line_1);
      }
      if (this.application.license.location.address_line_2) {
        addressStreetParts.push(this.application.license.location.address_line_2);
      }
      return addressStreetParts.join(', ');
    },
    addressCityPart() {
      if (!this.application.id) {
        return '';
      }
      let addressCityParts = [];
      if (this.application.license.location.address_city) {
        addressCityParts.push(this.application.license.location.address_city);
      }
      if (this.application.license.location.address_state) {
        addressCityParts.push(this.application.license.location.address_state);
      }
      if (this.application.license.location.address_zip) {
        addressCityParts.push(this.application.license.location.address_zip);
      }
      return addressCityParts.join(', ');
    }
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_APPLICATIONS}/${this.id}`).then(({data}) => {
      this.application = data.application;
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-applications"});
    }).finally(() => this.loading = false);
  }
}
</script>
